/** /notifications/canned — the notification pack's archetype catalog.
 *
 *  Read-only: these rows are materialised from code by `manage.py pack_seed`,
 *  so the page shows what is on offer and what it would build, and never edits
 *  the recipe. The write is "Materialise", per archetype or per group.
 *
 *  The grid is deliberately not sortable beyond the backend's whitelist
 *  (order / codename / name / event / id): a header that silently falls back to
 *  the default is a dead control.
 */
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import * as cannedApi from '@/api/cannedNotifications';
import type { CannedGroup, CannedNotificationListItem } from '@/api/cannedNotifications';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { PageHeader } from '@/components/PageHeader';
import { usePermissions } from '@/hooks/usePermissions';
import { PAGE_FILL_SX } from '@/layout/pageFill';

import { CannedNotificationDetailDialog } from './CannedNotificationPanel';
import { MaterialiseDialog } from './MaterialiseDialog';

export function CannedNotificationsListPage() {
  const { hasPerm } = usePermissions();
  const canMaterialise = hasPerm('console_canned_notifications_materialise');

  const [target, setTarget] = useState<{
    canned?: CannedNotificationListItem;
    group?: CannedGroup;
  } | null>(null);
  const [inspecting, setInspecting] = useState<CannedNotificationListItem | null>(null);

  const groupsQuery = useQuery({
    queryKey: ['canned_notification_groups'],
    queryFn: () => cannedApi.listGroups(),
  });
  const groups = groupsQuery.data?.results ?? [];

  const columns = useMemo<GridColDef<CannedNotificationListItem>[]>(
    () => [
      copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
      { field: 'codename', headerName: 'Codename', flex: 1, minWidth: 190 },
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 180 },
      {
        field: 'event',
        headerName: 'Event',
        flex: 1,
        minWidth: 200,
        renderCell: (p) => <code>{p.row.event}</code>,
      },
      {
        field: 'channels',
        headerName: 'Channels',
        width: 180,
        sortable: false,
        renderCell: (p) => (
          <Stack direction="row" spacing={0.5}>
            {p.row.channels.map((c) => (
              <Chip key={c} size="small" label={c} variant="outlined" />
            ))}
          </Stack>
        ),
      },
      {
        field: 'materialised_count',
        headerName: 'Tenants',
        width: 100,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        renderCell: (p) => (
          <Tooltip title="Operators that have this message materialised.">
            <span>{p.row.materialised_count ?? 0}</span>
          </Tooltip>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: (p) =>
          canMaterialise ? (
            <Button
              size="small"
              onClick={(e) => {
                // The row opens the detail dialog; this button must not.
                e.stopPropagation();
                setTarget({ canned: p.row });
              }}
            >
              Materialise…
            </Button>
          ) : null,
      },
    ],
    [canMaterialise],
  );

  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      cannedApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? 'order',
        search: params.search,
      }),
    [],
  );

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="canned-notifications"
        title="Canned Notifications"
        subtitle="Ready-made guest messages a new operator can switch on instead of authoring. Materialised per operator; the blocks, lists and rulesets stay shared."
      />

      {groupsQuery.isSuccess && groups.length === 0 && (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
          Nothing is seeded in this environment yet. Run <code>manage.py pack_seed</code> on the
          backend — until then there is nothing to materialise.
        </Alert>
      )}

      {groups.length > 0 && (
        <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
          {groups.map((g) => (
            <Box
              key={g.id}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                minWidth: 320,
                flex: '1 1 320px',
              }}
            >
              <Typography variant="subtitle1">{g.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {g.description}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {g.notifications.length} message{g.notifications.length === 1 ? '' : 's'} · pack{' '}
                {g.pack_version || '—'}
              </Typography>
              {canMaterialise && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<RocketLaunchIcon />}
                    onClick={() => setTarget({ group: g })}
                  >
                    Materialise group…
                  </Button>
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      )}

      <ResourceDataGrid<CannedNotificationListItem>
        queryKey={['canned_notifications']}
        queryFn={queryFn}
        columns={columns}
        defaultSort={{ field: 'order', sort: 'asc' }}
        onRowClick={(_id, row) => setInspecting(row)}
        emptyMessage="No canned notifications seeded."
        headerToolbar={{
          search: true,
          searchPlaceholder: 'Codename, name…',
          exportFileName: 'canned-notifications',
        }}
      />

      {inspecting && (
        <CannedNotificationDetailDialog row={inspecting} onClose={() => setInspecting(null)} />
      )}

      {target && (
        <MaterialiseDialog
          open
          canned={target.canned ?? null}
          group={target.group ?? null}
          onClose={() => setTarget(null)}
          onDone={() => groupsQuery.refetch()}
        />
      )}
    </Box>
  );
}
